import React, { useEffect, useState } from "react";
import {
  Save,
  RotateCcw,
  AlertCircle,
  CheckCircle2,
  Scale,
} from "lucide-react";
import { useLegal, setLegal, resetLegal } from "@/lib/brandingStore";

const FIELDS = [
  { name: "establishment_name", label: "Nom de l'établissement" },
  { name: "legal_name", label: "Nom juridique / société", placeholder: "À compléter" },
  { name: "legal_form", label: "Forme juridique", placeholder: "Ex. SAS, SARL, EI…" },
  { name: "siret", label: "SIRET", placeholder: "À compléter" },
  { name: "rcs", label: "RCS (si applicable)", placeholder: "À compléter" },
  { name: "address_street", label: "Adresse (rue)" },
  { name: "address_zip_city", label: "Code postal · Ville" },
  { name: "phone", label: "Téléphone" },
  { name: "email", label: "Email" },
  { name: "domain", label: "Nom de domaine" },
  { name: "publication_director", label: "Responsable de publication", placeholder: "À compléter" },
  { name: "host_provider", label: "Hébergeur", placeholder: "À compléter (nom + adresse)" },
  { name: "contact_email", label: "Email de contact" },
];

const Field = ({ name, label, value, onChange, placeholder, kind = "text" }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-street">
      {label}
    </span>
    {kind === "textarea" ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={`admin-legal-${name}`}
        rows={3}
        className="mt-1 w-full rounded-md border border-[#262626] bg-[#0A0A0A] py-2 px-3 text-sm text-white placeholder:text-white/30 focus:border-[#FF9500] focus:outline-none transition-colors resize-y"
      />
    ) : (
      <input
        type="text"
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder || ""}
        data-testid={`admin-legal-${name}`}
        className="mt-1 w-full rounded-md border border-[#262626] bg-[#0A0A0A] py-2 px-3 text-sm text-white placeholder:text-white/30 focus:border-[#FF9500] focus:outline-none transition-colors"
      />
    )}
  </label>
);

export const AdminLegal = () => {
  const current = useLegal();
  const [form, setForm] = useState(current);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setForm(current);
  }, [current]);

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setSaved(false);
  };

  const save = (e) => {
    e.preventDefault();
    setLegal(form);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2500);
  };

  const reset = () => {
    if (!window.confirm("Restaurer les valeurs par défaut ?")) return;
    resetLegal();
  };

  return (
    <div data-testid="admin-page-legal" className="space-y-8">
      <header>
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#FF9500] font-street inline-flex items-center gap-2">
          <Scale size={12} /> Mentions légales
        </div>
        <h1 className="font-street mt-2 text-3xl tracking-wide">Informations légales</h1>
        <p className="mt-2 text-sm text-white/55 leading-snug max-w-2xl">
          Renseignez ici les informations qui apparaissent sur la page publique
          <code className="mx-1 text-white/80">/mentions-legales</code>. Laissez vide les champs inconnus —
          la page affichera "À compléter" automatiquement.
        </p>
      </header>

      <form onSubmit={save} className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map((f) => (
            <Field
              key={f.name}
              name={f.name}
              label={f.label}
              value={form[f.name] || ""}
              onChange={onChange}
              placeholder={f.placeholder}
            />
          ))}
        </div>

        <Field
          name="additional_notice"
          label="Mention complémentaire (optionnel)"
          value={form.additional_notice || ""}
          onChange={onChange}
          placeholder="Ex. propriété intellectuelle, cookies, RGPD…"
          kind="textarea"
        />

        {saved && (
          <div
            role="status"
            data-testid="admin-legal-saved"
            className="inline-flex items-center gap-2 rounded-md border border-[#22C55E]/40 bg-[#22C55E]/10 px-3 py-2 text-[12px] text-[#86EFAC]"
          >
            <CheckCircle2 size={13} /> Mentions enregistrées.
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="submit"
            data-testid="admin-legal-save"
            className="font-street inline-flex items-center gap-2 rounded-md bg-[#FF9500] px-5 py-2.5 text-sm tracking-wider text-black shadow-[0_4px_0_#7a4900] hover:-translate-y-0.5 transition-all"
          >
            <Save size={14} />
            Enregistrer
          </button>
          <button
            type="button"
            onClick={reset}
            data-testid="admin-legal-reset"
            className="font-street inline-flex items-center gap-2 rounded-md border border-[#262626] px-4 py-2.5 text-sm tracking-wider text-white/70 hover:text-white hover:border-white/30 transition-colors"
          >
            <RotateCcw size={13} /> Réinitialiser
          </button>
        </div>

        <div className="text-[11px] text-white/40 leading-snug">
          <AlertCircle size={11} className="inline-block mr-1 -mt-0.5" />
          Données stockées localement (Phase 0). Ne pas inventer SIRET ni
          hébergeur — laissez "À compléter" tant que l'information officielle
          n'est pas disponible.
        </div>
      </form>
    </div>
  );
};

export default AdminLegal;
